import type {FC} from 'react'
import {useEffect, useRef} from 'react'
import {
  useReportScreenCaptureFailureMutation,
  useUploadScreenCaptureMutation,
} from '@apis/aiCoreAPI'
import type {WSEvent, WSScreenCaptureRequest} from '@interfaces/alpinist/ws'
import {usePageContextRegistry} from '@shared/alpinist/pageContext'
import {captureScreen} from '@shared/alpinist/screenCapture/captureScreen'
import {addLiveCapture} from '@shared/alpinist/screenCapture/liveCaptures'
import {useConversationEvents} from './ConversationStreamProvider'

// The backend expires a request 30 s after making it. A request older than that
// is a replay (the socket's reconnect cache) and is not answered.
export const CAPTURE_REQUEST_MAX_AGE_MS = 30_000

const requestDeadline = (evt: WSScreenCaptureRequest): number => {
  const expiresAt = evt.payload.expires_at ? Date.parse(evt.payload.expires_at) : NaN
  if (!Number.isNaN(expiresAt)) return expiresAt
  const at = Date.parse(evt.at)
  return Number.isNaN(at) ? Infinity : at + CAPTURE_REQUEST_MAX_AGE_MS
}

export const isFreshCaptureRequest = (evt: WSScreenCaptureRequest, now: number): boolean =>
  requestDeadline(evt) > now

/** Resolves true once the tab is visible, false if `deadline` passes first. */
export const waitUntilVisible = (deadline: number): Promise<boolean> =>
  new Promise((resolve) => {
    if (document.visibilityState === 'visible') return resolve(true)
    const finish = (visible: boolean): void => {
      document.removeEventListener('visibilitychange', onChange)
      clearTimeout(timer)
      resolve(visible)
    }
    const onChange = (): void => {
      if (document.visibilityState === 'visible') finish(true)
    }
    const timer = setTimeout(() => finish(false), Math.max(0, deadline - Date.now()))
    document.addEventListener('visibilitychange', onChange)
  })

const errorText = (error: unknown): string =>
  (error instanceof Error ? `${error.name}: ${error.message}` : String(error)).slice(0, 150)

type Props = {conversationId: string}

/**
 * Answers look_at_screen (AB#6762, layer 2). Mounted only while the Alpinist
 * panel is open in side mode, so a closed or full-width panel never answers.
 * Each request is answered at most once.
 *
 * When it cannot capture it says why, so the backend logs it and Alpinist can
 * tell the user, instead of a bare "screen not available" after 15 s: a hidden
 * tab or a request that arrived too late only notes the reason (another window
 * may still answer, and a tab that turns visible in time still captures); a
 * capture that throws ends the request.
 */
const ScreenCaptureResponder: FC<Props> = ({conversationId}) => {
  const {subscribe} = useConversationEvents()
  const registry = usePageContextRegistry()
  const [upload] = useUploadScreenCaptureMutation()
  const [reportFailure] = useReportScreenCaptureFailureMutation()
  const answeredRef = useRef(new Set<string>())

  useEffect(() => {
    const report = (requestId: string, error: string): void => {
      void reportFailure({conversation_id: conversationId, request_id: requestId, error})
        .unwrap()
        .catch(() => undefined) // the request is gone or answered: nothing to add
    }

    const answer = async (evt: WSScreenCaptureRequest): Promise<void> => {
      const requestId = evt.payload.request_id
      if (typeof requestId !== 'string' || answeredRef.current.has(requestId)) return
      answeredRef.current.add(requestId)
      const deadline = requestDeadline(evt)
      if (deadline <= Date.now()) {
        report(requestId, 'stale')
        return
      }
      if (document.visibilityState !== 'visible') {
        report(requestId, 'hidden_tab')
        if (!(await waitUntilVisible(deadline))) return
      }

      let capture
      try {
        capture = await captureScreen()
      } catch (error) {
        console.warn('[Alpinist] screen capture failed', error)
        report(requestId, `capture_error: ${errorText(error)}`)
        return
      }
      try {
        await upload({
          conversation_id: conversationId,
          request_id: requestId,
          image: capture.image,
          text: capture.text,
          page_context: registry?.read(),
        }).unwrap()
        // Shown in the chat at once, while Alpinist is still looking.
        addLiveCapture(conversationId, requestId, capture.image)
      } catch (error) {
        // Refused (already answered, expired) or the network: the backend has
        // the request's state, and Alpinist hears it when its wait runs out.
        console.warn('[Alpinist] screen capture upload failed', error)
      }
    }

    return subscribe({
      onMessage: (evt: WSEvent) => {
        // The socket is the conversation's own, so every request on it is ours.
        if (evt.kind === 'screen.capture.request') {
          void answer(evt)
        }
      },
    })
  }, [conversationId, registry, subscribe, upload, reportFailure])

  return null
}

export default ScreenCaptureResponder
